import { configuredCarInputToPrString } from '@oneaudi/onegraph-utils';
import { useAtom, useAtomValue } from 'jotai';
import { useEffect } from 'react';
import { ServiceFooter } from '~/app/compositions/content/service-footer/service-footer';
import { useAppServices, useAppState } from '~/app/contexts/app-context';
import { useI18n } from '~/app/i18n/i18n';
import { useMarketConfig } from '~/app/market-config';
import getAvpStageLink from '~/app/market-config/get-avp-stage-link';
import { FinancialProductResponse } from '~/app/market-config/models/financial-product-response';
import { useMarketUiConfig } from '~/app/market-config/use-market-ui-config';
import CarConfiPage from '~/app/pages/car-confi-page';
import { ResponseNotification } from '~/app/pages/components/notification/notification';
import { useSaveConfigurationNotification } from '~/app/pages/components/notification/use-notification';
import { StockCarDialog } from '~/app/pages/configurator/components/dialogs/stock-car-dialog';
import { MoreInformationDialog } from '~/app/pages/configurator/components/more-information-dialog';
import { CtxConfiguratorProvider } from '~/app/pages/configurator/context/ctx-configurator';
import {
	saveAudicode,
	saveAudicodeUCAS,
} from '~/app/pages/summary/components/save-configuration/components/save-configuration';
import SummaryPage from '~/app/pages/summary/summary';
import { useInitTrackingService } from '~/app/tracking/use-init-tracking-service';
import { getMonthlyRateDisclaimer } from '~/app/utils/get-monthly-rate-disclaimer';
import isConsFinancingStage from '~/app/utils/is-cons-financing-stage';
import { MessageDialog } from '~/components/dialog';
import { IframeDialog } from '~/components/dialog/iframe-dialog/iframe-dialog';
import { RescueInfoDialog } from '~/components/dialog/rescue-info-dialog/rescue-info-dialog';
import { TermsAndConditionsDialog } from '~/components/dialog/terms-and-conditions-dialog/terms-and-conditions-dialog';
import { Disclaimer } from '~/components/disclaimer/disclaimer';
import { useConfigurator } from '~/graphql/hooks/use-configurator';
import { useGetDefaultIdentifer } from '~/graphql/hooks/use-default-identifier';
import { useFinancing } from '~/graphql/hooks/use-financing';
import { state } from '~/storage';
import { Container, InnerContainer } from '~/styles/container';

export default function MarketRouter() {
	const currentView = useAtomValue(state.currentView);
	const [, setDebugMode] = useAtom(state.isDebugMode);
	const {
		featureServices: { authService },
	} = useAppServices();
	const carConfig = useConfigurator();
	const marketConfig = useMarketConfig();
	const marketUIConfig = useMarketUiConfig();
	const [, setDisclaimer] = useAtom(state.financingDisclaimerText);
	const preset = useAtomValue(state.fsagFinancialCalculatorSettings);
	const { financialCalculator, carIdentifier, isLoading } = useConfigurator();
	const culture = `${marketConfig.language}-${marketConfig.market.toUpperCase()}`;
	const [finProducts, setFinProducts] = useAtom(state.financialProducts);
	const [, setDefaultFinanceProduct] = useAtom(state.defaultFinancialProduct);
	const [, setAvpConfigId] = useAtom(state.AVPConfigId);
	const { i18n } = useI18n();
	const [financialError] = useAtom(state.financialError);
	const { getFinancing } = useFinancing();
	const { notificationData, financingData } = useAppState();
	const { showNotification } = useSaveConfigurationNotification();
	const { getDefaultIdentifier } = useGetDefaultIdentifer();
	const [calculatorScriptReady] = useAtom(state.calculatorScriptReady);
	useInitTrackingService();

	useEffect(() => {
		const urlParams = new URLSearchParams(window.location.search);
		const debugMode = urlParams.get('debug') ?? '';
		setDebugMode(debugMode.toLowerCase() === 'true');

		urlParams.set('pr', configuredCarInputToPrString(carConfig?.carIdentifier));
		const newUrl = `${window.location.pathname}?${urlParams.toString()}${window.location.hash}`;
		window.history.replaceState({ path: newUrl }, '', newUrl);
	}, [JSON.stringify(carConfig?.carIdentifier), setDebugMode]);

	useEffect(() => {
		if (carIdentifier.model.year) {
			setAvpConfigId(
				getAvpStageLink(marketConfig.config.carlineId, carIdentifier.model.year, carIdentifier.model.code),
			);
		}
	}, [isLoading, JSON.stringify(carConfig?.carIdentifier)]);

	useEffect(() => {
		if (marketConfig.config.enableFinancingServices && !financialError) {
			getFinancing();
		}
	}, [JSON.stringify(carIdentifier), marketConfig.config.financingInput]);

	useEffect(() => {
		if (!marketConfig.config.enableFinancingServices) return;
		if (financingData?.loading) return;
		if (!financingData?.financing?.meta?.externalRequestOptions?.Vehicle) return;

		if (finProducts.length > 0) return;

		setDefaultFinanceProduct(financingData?.financing?.meta?.externalRequestOptions.Product['@ID']);
		const request = {
			Request: {
				'@Domain': financingData?.financing?.meta?.externalRequestOptions['@Domain']
					? financingData?.financing?.meta?.externalRequestOptions['@Domain']
					: marketConfig.config.financeDomain,
				'@Culture': culture,
				Vehicle: {
					...financingData?.financing?.meta?.externalRequestOptions?.Vehicle,
				},
				Product: {
					'@ID': financingData?.financing?.meta?.externalRequestOptions?.Product['@ID'],
				},
			},
		};
		if (!financialCalculator) return;

		const calculator = isConsFinancingStage()
			? new financialCalculator({
					WEBCALC_API_URL: 'https://api.cons.webcalc.vwfs.io/webcalc-frontend-service',
					WEBCALC_API_STAGE: 'cons',
				})
			: new financialCalculator();

		const potentialDisclaimer = getMonthlyRateDisclaimer(
			marketConfig.config.financingDisclaimer,
			financingData?.financing?.disclaimers,
		);
		setDisclaimer(potentialDisclaimer);

		calculator
			.getProductsAsync(request)
			.then((result: FinancialProductResponse) => {
				const { products } = result;
				const tmpProds: Array<{ key: string; val: string }> = [];
				products.forEach((item) => {
					tmpProds.push({ key: item.id, val: item.label });
				});
				if (tmpProds.length > 0) {
					setFinProducts(tmpProds);
				}
			})
			.catch(() => {
				console.error('Unexpected issue loading financial products. ');
			});
	}, [
		JSON.stringify(carIdentifier),
		JSON.stringify(marketConfig.config.carIdentifier),
		marketConfig.config.enableFinancingServices,
		marketConfig.config.financeDomain,
		JSON.stringify(marketConfig.config.financingDisclaimer),
		culture,
		financingData?.loading,
		JSON.stringify(financingData?.financing?.meta?.externalRequestOptions.Vehicle),
		finProducts.length,
		JSON.stringify(financialCalculator),
		marketConfig.market,
		JSON.stringify(preset),
		setDisclaimer,
		setFinProducts,
		i18n,
		calculatorScriptReady,
	]);

	useEffect(() => {
		const saveConfigCode = new URLSearchParams(window.location.search).get('saveconfig');
		if (saveConfigCode && Boolean(i18n('ngc.saveConfiguration.myAudi.wishlist.url'))) {
			const save = async () => {
				const url = new URL(window.document.location.href);
				url.searchParams.delete('saveconfig');
				window.history.pushState({ path: url.toString() }, '', url.toString());
				const result = await saveAudicode(saveConfigCode, authService, i18n);
				if (marketConfig.market === 'DE') {
					await saveAudicodeUCAS(saveConfigCode, authService);
				}
				showNotification((_state) => ({ ..._state, ...result }));
			};
			save();
		}
	}, []);

	useEffect(() => {
		getDefaultIdentifier();
	}, []);

	const { globalDisclaimer } = marketConfig.config;

	return (
		<>
			{globalDisclaimer && globalDisclaimer.disclaimer && globalDisclaimer.variant && (
				<Disclaimer type="global" content={globalDisclaimer.disclaimer} variant={globalDisclaimer.variant} />
			)}
			{notificationData && notificationData.copy && notificationData.title && <ResponseNotification />}
			<main id="content-area" style={{ position: 'relative' }}>
				<CtxConfiguratorProvider>
					<Container>
						<InnerContainer removeSpaces={true}>
							{currentView === 'configurator' && (
								<CarConfiPage market={marketConfig.config} marketUi={marketUIConfig} />
							)}
							{currentView === 'summary' && <SummaryPage />}
							<StockCarDialog />
						</InnerContainer>
					</Container>
					{currentView === 'configurator' && <ServiceFooter marketUi={marketUIConfig} />}
				</CtxConfiguratorProvider>
			</main>
			<RescueInfoDialog />
			<MessageDialog />
			<MoreInformationDialog />
			<TermsAndConditionsDialog />
			<IframeDialog />
		</>
	);
}
